import React from "react";
import {
  Box,
  Divider,
  Paper,
  Grid,
  Typography,
  Stack,
  alpha,
} from "@mui/material";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import EditIcon from "@mui/icons-material/Edit";
import PaymentsIcon from "@mui/icons-material/Payments";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButtonWithTooltip from "../../../utils/IconButtonWithTooltip";
import ReceiptIcon from "@mui/icons-material/Receipt";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PercentIcon from "@mui/icons-material/Percent";
import CategoryIcon from "@mui/icons-material/Category";
import TodayIcon from "@mui/icons-material/Today";

const AssetInfo = ({
  assetCodeList,
  selectedItem,
  onEditAsset,
  onViewPrincipalPaid,
  onDeleteAsset,
  isSmallScreen,
  canEdit = true,
}) => {
  if (!selectedItem) {
    return null;
  }

  const assetCode =
    assetCodeList?.find((item) => item.assetId === selectedItem.assetId)
      ?.assetCode || null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
      }}
    >
      {/* Header and Actions Stack */}
      <Stack
        direction='row'
        spacing={2}
        justifyContent='space-between'
        alignItems='center'
        sx={{ mb: 2 }} // Removed mb from Typography and added it here
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='h6' fontWeight={600} color='primary.main'>
            Asset Information
          </Typography>

          {assetCode && (
            <Typography variant='subtitle2' color='text.secondary'>
              (Asset Code: {assetCode})
            </Typography>
          )}
        </Stack>

        {canEdit && (
          <Stack direction='row' spacing={1}>
            <IconButtonWithTooltip
              icon={EditIcon}
              tooltipTitle='Edit Asset Info'
              onClick={onEditAsset}
              color='primary'
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                "&:hover": {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                },
              }}
            />
            <IconButtonWithTooltip
              icon={PaymentsIcon} // Renamed to avoid collision with variable name
              tooltipTitle='Define Principal Paid'
              onClick={onViewPrincipalPaid}
              color='secondary'
              sx={{
                bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08),
                "&:hover": {
                  bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                },
              }}
            />
            <IconButtonWithTooltip
              icon={DeleteIcon}
              tooltipTitle='Delete Asset'
              onClick={onDeleteAsset}
              color='error'
              sx={{
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                "&:hover": {
                  bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
                },
              }}
            />
          </Stack>
        )}
      </Stack>

      {/* Horizontal Divider */}
      <Divider sx={{ mb: 3 }} />

      {/* Asset Info Fields Grid */}
      <Grid container spacing={3} alignItems='flex-start'>
        <Grid item xs={12}>
          <Stack
            direction={isSmallScreen ? "column" : "row"}
            spacing={isSmallScreen ? 2 : 3}
            alignItems='flex-start'
            divider={
              !isSmallScreen && <Divider orientation='vertical' flexItem />
            }
          >
            {/* Asset Info Field Component (repeated) */}
            {[
              {
                label: "Name",
                value: selectedItem.assetName,
                icon: ReceiptIcon,
              },
              {
                label: "Category",
                value: selectedItem.categoryName,
                icon: CategoryIcon,
              },
              {
                label: "CA Type",
                value: selectedItem.caType,
                icon: EventAvailableIcon,
              },
              {
                label: "No. of years of claim",
                value: selectedItem.noOfYears,
                icon: TodayIcon,
              },
              {
                label: "Cost",
                value: formatAmount(selectedItem.cost),
                icon: AttachMoneyIcon,
              },
              ...(selectedItem.initialAllowanceRate
                ? [
                    {
                      label: "Initial Allowance Rate",
                      value: `${selectedItem.initialAllowanceRate}%`,
                      icon: PercentIcon,
                    },
                  ]
                : []),
            ].map((field, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  boxShadow: "none",
                  border: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  flexGrow: 1, // Allows papers to fill space
                  gap: 1,
                }}
              >
                <Box display='flex' alignItems='center' gap={1}>
                  <field.icon color='primary' fontSize='small' />
                  <Typography variant='caption' color='text.secondary'>
                    {field.label}
                  </Typography>
                </Box>
                <Typography
                  variant='body1'
                  color='text.primary'
                  fontWeight={500}
                >
                  {field.value || "N/A"}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AssetInfo;
